import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Receipt, Mail, FileText, AlertTriangle, CheckCircle, Tag, X, Plus, Pencil, Eye, Package, Check,
} from 'lucide-react';
import InvoicePreviewModal from '../components/InvoicePreviewModal';
import toast from 'react-hot-toast';
import api from '../api';
import { downloadInvoicePDF, STATUS_LABELS } from '../utils/exportHelpers';
import ClientFormModal from '../components/ClientFormModal';
import NotesEditor from '../components/NotesEditor';
import { can } from '../auth';

const Rand = (v) => `R${parseFloat(v || 0).toFixed(2)}`;
const dmy = (d) => (d ? new Date(d).toLocaleDateString('en-ZA') : '—');

const INVOICE_STATUS = {
  unpaid:    'bg-red-100 text-red-700',
  part_paid: 'bg-amber-100 text-amber-700',
  paid:      'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500',
};
const INVOICE_LABEL = {
  unpaid: 'Unpaid', part_paid: 'Part paid', paid: 'Paid', cancelled: 'Cancelled',
};
// Parcel status pill colours, as on the Billing and Parcels pages
const STATUS_COLORS = {
  created:          'bg-gray-100 text-gray-700',
  collected:        'bg-blue-100 text-blue-700',
  at_depot:         'bg-purple-100 text-purple-700',
  out_for_delivery: 'bg-yellow-100 text-yellow-700',
  delivered:        'bg-green-100 text-green-700',
  failed_delivery:  'bg-red-100 text-red-700',
  returned_to_depot:'bg-orange-100 text-orange-700',
  rescheduled:      'bg-indigo-100 text-indigo-700',
  cancelled:        'bg-red-200 text-red-900',
};

// Add/remove the words we look for in a Capitec statement line to recognise
// this client. Saved through a keywords-only endpoint, so nothing else on the
// client record can be touched from here.
function KeywordEditor({ clientId, keywords, conflicts = [] }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async (next, message) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/clients/${clientId}/keywords`, { match_keywords: next });
      qc.invalidateQueries({ queryKey: ['client-account', clientId] });
      if (message) toast.success(message);
      // A shared keyword can't identify who paid — say so loudly and for longer
      for (const c of data.conflicts || []) {
        toast.error(
          `"${c.keywords.join('", "')}" is also on ${c.client}. A payment mentioning it can't tell you ` +
          `which of them paid — make each keyword unique.`,
          { duration: 8000 }
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save keywords');
    } finally {
      setBusy(false);
    }
  };

  const add = () => {
    const k = adding.trim();
    if (k.length < 2) { toast.error('A keyword needs at least 2 characters'); return; }
    if (keywords.some((x) => x.toLowerCase() === k.toLowerCase())) {
      toast.error(`"${k}" is already there`); setAdding(''); return;
    }
    setAdding('');
    save([...keywords, k], `Payments mentioning "${k}" will now match this client`);
  };

  const remove = (k) => save(keywords.filter((x) => x !== k));

  const tooShort = adding.trim().length > 0 && adding.trim().length < 4;

  return (
    <div className="flex items-start gap-2">
      <Tag size={14} className="text-gray-400 mt-2 flex-shrink-0" />
      <div className="flex-1">
        <p className="text-xs text-gray-500 mb-1.5">
          Bank statement keywords — a Capitec payment mentioning any of these is matched to this client
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          {keywords.map((k) => (
            <span key={k} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs pl-2 pr-1 py-1 rounded">
              {k}
              <button onClick={() => remove(k)} disabled={busy}
                title={`Stop matching "${k}"`} className="text-blue-300 hover:text-red-500 disabled:opacity-50">
                <X size={12} />
              </button>
            </span>
          ))}
          <input value={adding} onChange={(e) => setAdding(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
            placeholder={keywords.length ? 'Add another…' : 'e.g. It Direct'}
            className="border rounded-lg px-2 py-1 text-xs w-36 focus:outline-none focus:ring-2 focus:ring-orange-400" />
          <button onClick={add} disabled={busy || !adding.trim()}
            className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs px-2 py-1 rounded disabled:opacity-40">
            <Plus size={12} /> Add
          </button>
        </div>
        {conflicts.length > 0 && (
          <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            {conflicts.map((c) => (
              <p key={c.client} className="text-xs text-amber-800">
                <strong>“{c.keywords.join('”, “')}”</strong> is also a keyword on <strong>{c.client}</strong>.
                A payment mentioning it can&apos;t tell you which of them paid, so it won&apos;t be matched
                automatically. Give each client a keyword only they use.
              </p>
            ))}
          </div>
        )}
        {tooShort ? (
          <p className="text-xs text-amber-600 mt-1.5">
            Short keywords turn up in other people&apos;s references and match the wrong client — use their
            full trading name if you can.
          </p>
        ) : keywords.length === 0 && (
          <p className="text-xs text-gray-400 mt-1.5">
            None yet. Use what actually appears on the statement — for this client that&apos;s usually their
            trading name, or the reference they always use.
          </p>
        )}
      </div>
    </div>
  );
}

// ── Status bar: the account and its parcels at a glance ──────────────────────
const STATUS_STRIP = {
  overdue:     { cls: 'bg-red-600 text-white',     icon: AlertTriangle, text: (b) => `R${b.money.overdue.toFixed(2)} overdue · oldest ${b.oldest_overdue_days} day${b.oldest_overdue_days === 1 ? '' : 's'}` },
  cod:         { cls: 'bg-amber-500 text-white',   icon: Receipt,       text: () => 'Cash on delivery — pays per parcel' },
  no_activity: { cls: 'bg-gray-500 text-white',    icon: Tag,           text: (b) => `No parcels in 90 days${b.parcels.last_parcel_at ? ` · last ${dmy(b.parcels.last_parcel_at)}` : ''}` },
  new:         { cls: 'bg-blue-600 text-white',    icon: Tag,           text: () => 'New client — no parcels yet' },
  good:        { cls: 'bg-green-600 text-white',   icon: CheckCircle,   text: (b) => b.money.balance > 0.005 ? `Account in good standing · R${b.money.balance.toFixed(2)} owing, nothing overdue` : 'Account in good standing — nothing owing' },
};

function Tile({ label, value, sub, tone = 'bg-white text-gray-800', to, onClick, title }) {
  const body = (
    <>
      <p className="text-base font-bold leading-tight">{value}</p>
      <p className="text-[11px] mt-0.5 opacity-75">{label}</p>
      {sub && <p className="text-[10px] opacity-60 truncate">{sub}</p>}
    </>
  );
  const cls = `rounded-lg border px-3 py-2 min-w-0 ${tone} ${to || onClick ? 'hover:ring-2 hover:ring-orange-300 cursor-pointer' : ''}`;
  if (onClick) return <button type="button" onClick={onClick} className={`${cls} text-left`} title={title}>{body}</button>;
  return to ? <Link to={to} className={cls} title={title}>{body}</Link> : <div className={cls} title={title}>{body}</div>;
}

const relDays = (d) => {
  if (!d) return null;
  const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  return days === 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`;
};

function ClientStatusBar({ client, bar, onShowUnbilled }) {
  if (!bar) return null;
  const strip = STATUS_STRIP[bar.account_status] || STATUS_STRIP.good;
  const Icon = strip.icon;
  const m = bar.money, p = bar.parcels, n = bar.notify;
  const chip = (on, label, offTitle) => (
    <span title={on ? `${label} notifications are on` : offTitle || `${label} notifications are off`}
      className={`text-[11px] px-2 py-0.5 rounded-full ${on ? 'bg-white/25 text-white' : 'bg-black/10 text-white/60 line-through'}`}>
      {label}
    </span>
  );

  return (
    <div className="rounded-xl overflow-hidden shadow-sm border">
      <div className={`flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 ${strip.cls}`}>
        <span className="flex items-center gap-2 font-semibold text-sm">
          <Icon size={16} /> {strip.text(bar)}
        </span>
        <span className="flex flex-wrap items-center gap-1.5 ml-auto">
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/25">
            {client.payment_terms === 'cod' ? 'COD' : `${client.payment_terms} days`}
          </span>
          {client.vat_number && <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/25">VAT reg.</span>}
          {client.pays_for_sent && <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/25">Pays for sent</span>}
          {chip(n.email && n.has_email, 'Email', n.has_email ? undefined : 'No email address on file')}
          {chip(n.whatsapp && n.has_phone, 'WhatsApp', n.has_phone ? undefined : 'No phone number on file')}
          {chip(n.telegram, 'Telegram')}
          {!n.has_email && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-black/20 font-medium" title="Invoices and statements cannot be emailed until an address is added">
              No email on file
            </span>
          )}
        </span>
      </div>

      <div className="bg-gray-50 p-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        <Tile label="Owing now" value={Rand(m.balance)}
          sub={m.unpaid_invoices ? `${m.unpaid_invoices} open invoice${m.unpaid_invoices === 1 ? '' : 's'}` : 'all settled'}
          tone={m.balance > 0.005 ? 'bg-orange-50 text-orange-800 border-orange-200' : 'bg-green-50 text-green-800 border-green-200'}
          to={m.unpaid_invoices ? `/invoices?client=${client.id}&status=unpaid` : undefined} title="Open the unpaid invoices" />
        <Tile label="Overdue" value={Rand(m.overdue)}
          sub={m.overdue > 0.005 ? `oldest due ${dmy(m.oldest_unpaid_due)}` : m.oldest_unpaid_due ? `next due ${dmy(m.oldest_unpaid_due)}` : ''}
          tone={m.overdue > 0.005 ? 'bg-red-50 text-red-800 border-red-200' : 'bg-white text-gray-600'} />
        <Tile label="Awaiting invoicing" value={p.delivered_uninvoiced ? `${p.delivered_uninvoiced} · ${Rand(p.uninvoiced_value)}` : '0'}
          sub={p.delivered_uninvoiced ? 'not yet on an invoice' : 'nothing waiting'}
          tone={p.delivered_uninvoiced ? 'bg-purple-50 text-purple-800 border-purple-200' : 'bg-white text-gray-600'}
          onClick={p.delivered_uninvoiced ? () => onShowUnbilled('ready') : undefined} title="Show the parcels ready to invoice, below" />
        <Tile label="On draft invoices" value={p.on_draft ? `${p.on_draft} · ${Rand(p.on_draft_value)}` : '0'}
          sub={p.on_draft ? 'issue the draft to bill them' : ''}
          tone={p.on_draft ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-white text-gray-600'}
          to={p.on_draft ? `/invoices?client=${client.id}&status=draft` : undefined} />
        <Tile label="Last payment" value={m.last_payment ? Rand(m.last_payment.amount) : '—'}
          sub={m.last_payment ? `${dmy(m.last_payment.paid_at)} · ${m.last_payment.method.toUpperCase()}` : 'none recorded'} />
        <Tile label="Last invoice" value={m.last_invoice ? m.last_invoice.number : '—'}
          sub={m.last_invoice
            ? `${dmy(m.last_invoice.issued_at)} · ${m.last_invoice_email?.status === 'sent' ? `emailed ${relDays(m.last_invoice_email.sent_at)}` : 'not emailed'}`
            : 'none yet'}
          to={m.last_invoice ? `/invoices?client=${client.id}` : undefined} />
        <Tile label="Usually pays in" value={m.avg_days_to_pay != null ? `${m.avg_days_to_pay} days` : '—'}
          sub={m.paid_sample ? `over the last ${m.paid_sample} paid invoice${m.paid_sample === 1 ? '' : 's'}` : 'no paid invoices yet'} />

        <Tile label="In transit now" value={p.active}
          sub={p.active ? 'booked, collected or out for delivery' : 'nothing on the road'}
          tone={p.active ? 'bg-blue-50 text-blue-800 border-blue-200' : 'bg-white text-gray-600'} />
        <Tile label="Delivered, last 30 days" value={p.delivered_30d}
          sub={p.total ? `${p.total} parcels all time` : ''} />
        <Tile label="Needs a price" value={p.needs_price}
          sub={p.needs_price ? 'cannot be invoiced until priced' : 'all priced'}
          tone={p.needs_price ? 'bg-red-50 text-red-800 border-red-200' : 'bg-white text-gray-600'}
          onClick={p.needs_price ? () => onShowUnbilled('needs_price') : undefined} title="Show the parcels still needing a price, below" />
        <Tile label="Last parcel" value={p.last_parcel_at ? relDays(p.last_parcel_at) : '—'}
          sub={p.last_parcel_at ? dmy(p.last_parcel_at) : 'no parcels yet'} />
        <Tile label="Sends / receives" value={`${p.sent} / ${p.received}`} sub="parcels as sender / as recipient" />
        <Tile label="Credited, 90 days" value={m.credit_notes_90d ? `−${Rand(m.credited_90d)}` : '—'}
          sub={m.credit_notes_90d ? `${m.credit_notes_90d} credit note${m.credit_notes_90d === 1 ? '' : 's'}` : 'no credit notes'}
          tone={m.credit_notes_90d ? 'bg-purple-50 text-purple-800 border-purple-200' : 'bg-white text-gray-600'} />
        <Tile label="Statements emailed" value={m.invoice_count ? (m.last_statement ? dmy(m.last_statement.sent_at) : 'never') : '—'}
          sub={m.last_statement ? `to ${m.last_statement.sent_to}` : `${m.invoice_count} invoice${m.invoice_count === 1 ? '' : 's'} all time`} />
      </div>
    </div>
  );
}


/* ── Unbilled parcels: what this client pays for that is not on a sent invoice ── */
const UNBILLED_FILTERS = [
  ['all', 'All', 'bg-gray-800 text-white'],
  ['needs_price', 'Needs a price', 'bg-red-600 text-white'],
  ['ready', 'Ready to invoice', 'bg-purple-600 text-white'],
  ['draft', 'On draft invoice', 'bg-amber-500 text-white'],
];
const groupOfParcel = (r) => (r.needs_price ? 'needs_price' : r.draft_invoice_id ? 'draft' : 'ready');

function UnbilledParcels({ client, rows, filter, onFilter }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [excluded, setExcluded] = useState({}); // tracking_number -> left off this invoice
  const [busy, setBusy] = useState(false);

  const counts = rows.reduce((acc, r) => { const g = groupOfParcel(r); acc[g] = (acc[g] || 0) + 1; return acc; }, {});
  const visible = rows.filter((r) => filter === 'all' || groupOfParcel(r) === filter);
  const ready = rows.filter((r) => groupOfParcel(r) === 'ready');
  const picked = ready.filter((r) => !excluded[r.tracking_number]);
  const amount = (r) => parseFloat(r.price || 0) + parseFloat(r.extras_total || 0);
  const pickedTotal = picked.reduce((s, r) => s + amount(r), 0);
  const canInvoice = can('billing.invoice') && ready.length > 0;

  const createInvoice = async (open) => {
    if (picked.length === 0) { toast.error('Every parcel is unticked — nothing to invoice'); return; }
    setBusy(true);
    try {
      const { data: inv } = await api.post('/billing/invoices', {
        client_id: client.id,
        tracking_numbers: picked.map((r) => r.tracking_number),
      });
      qc.invalidateQueries({ queryKey: ['client-account', client.id] });
      qc.invalidateQueries({ queryKey: ['ready-to-invoice'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['summary'] });
      setExcluded({});
      if (open) {
        // straight to the draft, expanded, so labels or a discount can be added and it issued
        navigate(`/invoices?client=${client.id}&status=draft&open=${inv.id}`);
        return;
      }
      toast.success(`Draft invoice saved for ${client.name} — ${Rand(inv.total)}. Open it under Invoices to add labels or a discount, then Issue.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not create that invoice');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div id="unbilled" className="bg-white rounded-xl shadow-sm overflow-hidden scroll-mt-4">
      <div className="px-5 pt-4 pb-2">
        <h2 className="font-semibold text-gray-700 flex items-center gap-2">
          <Package size={16} className="text-orange-500" /> Unbilled parcels
          {rows.length > 0 && <span className="text-xs font-medium bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{rows.length}</span>}
        </h2>
        <p className="text-xs text-gray-400 mt-0.5">
          Parcels this client pays for that are not on a sent invoice yet. Open a parcel to give it a price;
          tick the priced ones and create the draft invoice from here.
        </p>
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-8">
          <Check size={24} className="mx-auto mb-1.5 text-green-500" />
          <p className="text-sm text-gray-500">Nothing waiting — every parcel this client pays for is priced and invoiced.</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5 px-5 pb-3">
            {UNBILLED_FILTERS.map(([key, label, active]) => {
              const n = key === 'all' ? rows.length : (counts[key] || 0);
              if (key !== 'all' && n === 0) return null;
              return (
                <button key={key} type="button" onClick={() => onFilter(key)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${filter === key ? `${active} border-transparent` : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                  {label} <span className="opacity-70">{n}</span>
                </button>
              );
            })}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  {canInvoice && <th className="px-4 py-2 w-8" title="Ticked parcels go on the draft invoice"></th>}
                  <th className="px-4 py-2 text-left">Parcel</th>
                  <th className="px-4 py-2 text-left hidden sm:table-cell">Booked</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left hidden md:table-cell">From → To</th>
                  <th className="px-4 py-2 text-left hidden lg:table-cell">Description</th>
                  <th className="px-4 py-2 text-right">Price</th>
                  <th className="px-4 py-2 text-left">Billing</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visible.map((r) => {
                  const g = groupOfParcel(r);
                  const href = `/parcels/${r.tracking_number}`;
                  return (
                    <tr key={r.tracking_number} className="hover:bg-gray-50">
                      {canInvoice && (
                        <td className="px-4 py-2.5">
                          {g === 'ready' && (
                            <input type="checkbox" className="accent-orange-500" checked={!excluded[r.tracking_number]}
                              onChange={(e) => setExcluded((x) => ({ ...x, [r.tracking_number]: !e.target.checked }))}
                              title="Untick to hold this parcel back for a later invoice" />
                          )}
                        </td>
                      )}
                      <td className="px-4 py-2.5">
                        <Link to={href} className="font-mono text-xs font-semibold text-orange-500 hover:underline">{r.tracking_number}</Link>
                      </td>
                      <td className="px-4 py-2.5 text-gray-500 hidden sm:table-cell">{dmy(r.created_at)}</td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${STATUS_COLORS[r.status] || 'bg-gray-100 text-gray-700'}`}>
                          {STATUS_LABELS[r.status] || r.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600 hidden md:table-cell">
                        <span className="truncate block max-w-[16rem]" title={`${r.sender_name || '—'} → ${r.recipient_name || '—'}`}>
                          {r.sender_name || '—'} → {r.recipient_name || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-500 hidden lg:table-cell">
                        <span className="truncate block max-w-[14rem]" title={r.description || ''}>{r.description || '—'}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        {r.needs_price ? <span className="text-red-600 font-medium">—</span> : (
                          <>
                            <span className="font-medium text-gray-800">{Rand(amount(r))}</span>
                            {parseFloat(r.extras_total) > 0 && <span className="block text-[11px] text-gray-400">incl. {Rand(r.extras_total)} extras</span>}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        {g === 'needs_price' && (
                          <Link to={href} className="text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 px-2 py-1 rounded-lg inline-flex items-center gap-1"
                            title="Open the parcel to weigh and price it">
                            Needs a price → open parcel
                          </Link>
                        )}
                        {g === 'ready' && <span className="text-xs font-medium text-purple-700 bg-purple-50 px-2 py-1 rounded-lg">Ready to invoice</span>}
                        {g === 'draft' && (
                          <Link to={`/invoices?client=${client.id}&status=draft&open=${r.draft_invoice_id}`}
                            className="text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded-lg"
                            title="On a draft invoice — open it to issue">
                            On draft invoice → open
                          </Link>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {visible.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-400">Nothing in this group.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {canInvoice && (
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t bg-gray-50">
              <p className="text-sm text-gray-600">
                <strong>{picked.length}</strong> of {ready.length} ready parcel{ready.length === 1 ? '' : 's'} ticked · <strong>{Rand(pickedTotal)}</strong>
                <span className="text-xs text-gray-400 block">Untick any parcel to hold it back for next time.</span>
              </p>
              <div className="flex gap-2">
                <button type="button" onClick={() => createInvoice(false)} disabled={busy || picked.length === 0}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 disabled:opacity-50">
                  Create draft
                </button>
                <button type="button" onClick={() => createInvoice(true)} disabled={busy || picked.length === 0}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50">
                  <Receipt size={15} /> Create &amp; open
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);   // full client record for the edit form
  const [previewId, setPreviewId] = useState(null); // invoice shown in the preview popup
  const [unbilledFilter, setUnbilledFilter] = useState('all');
  const showUnbilled = (filter) => {
    setUnbilledFilter(filter);
    document.getElementById('unbilled')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // The account page only carries part of the client, so fetch the whole
  // record before opening the form — otherwise blank fields would look like
  // the office cleared them.
  const openEdit = async () => {
    try {
      const { data } = await api.get(`/clients/${id}`);
      setEditing(data);
    } catch {
      toast.error('Could not load this client for editing');
    }
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['client-account', id],
    queryFn: () => api.get(`/clients/${id}/account`).then((r) => r.data),
    // Always fresh: the office prices a parcel on its page and comes straight back here
    staleTime: 0,
  });

  // The invoice PDF needs the full invoice (lines + payments), so fetch on demand
  const openInvoicePdf = async (invoiceId) => {
    try {
      const [{ data: inv }, { data: settings }] = await Promise.all([
        api.get(`/billing/invoices/${invoiceId}`),
        api.get('/settings/billing'),
      ]);
      await downloadInvoicePDF(inv, settings);
      // a PDF taken for the client counts as printed — same as on the Invoices page
      if (inv.status !== 'draft' && inv.status !== 'cancelled') {
        await api.post(`/billing/invoices/${invoiceId}/printed`).catch(() => {});
        qc.invalidateQueries({ queryKey: ['client-account', id] });
      }
    } catch {
      toast.error('Could not build that invoice PDF');
    }
  };

  if (isLoading) return <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">Loading…</div>;
  if (error) return (
    <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">
      Could not load this client. <Link to="/clients" className="text-orange-500">Back to clients</Link>
    </div>
  );

  const { client, invoices, statements, keyword_conflicts, status_bar, unbilled = [] } = data;
  const statementUrl = client.statement_token
    ? `https://expressway.xcourier.co.za/api/public/statement/${client.statement_token}`
    : null;

  return (
    <div className="space-y-4">
      {previewId && (
        <InvoicePreviewModal invoiceId={previewId} onClose={() => setPreviewId(null)}
          onDownloaded={() => qc.invalidateQueries({ queryKey: ['client-account', id] })} />
      )}
      {editing && (
        <ClientFormModal existing={editing} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['client-account', id] }); }} />
      )}
      <Link to="/clients" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-orange-500">
        <ArrowLeft size={15} /> Back to clients
      </Link>

      {/* Status bar: account + parcel position at a glance */}
      <ClientStatusBar client={client} bar={status_bar} onShowUnbilled={showUnbilled} />

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-800">{client.name}</h1>
            <div className="text-sm text-gray-500 mt-1 space-y-0.5">
              {client.email && <p>{client.email}</p>}
              {client.phone && <p>{client.phone}</p>}
              {(client.address || client.city) && (
                <p className="text-xs text-gray-400">{[client.address, client.city, client.postal_code].filter(Boolean).join(', ')}</p>
              )}
            </div>
            <NotesEditor key={client.notes || ''} value={client.notes} editable={can('clients.edit')} className="mt-3"
              placeholder="Gate code, where to leave parcels, who to phone…"
              onSave={async (notes) => {
                await api.patch(`/clients/${client.id}`, { notes });
                qc.invalidateQueries({ queryKey: ['client-account', id] });
                qc.invalidateQueries({ queryKey: ['clients'] });
              }} />
            <div className="flex flex-wrap gap-2 mt-3">
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-lg">
                Terms: {client.payment_terms === 'cod' ? 'Cash on delivery' : `${client.payment_terms} days`}
              </span>
              {client.vat_number && (
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-lg">VAT {client.vat_number}</span>
              )}
              {client.pays_for_sent && (
                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-lg">
                  Pays for parcels they send
                </span>
              )}
              {client.pastel_code && (
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-lg" title="Sage Pastel customer account code (assigned on first export)">
                  Pastel: {client.pastel_code}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {can('clients.edit') && (
              <button onClick={openEdit}
                title="Change the address, phone, email, terms, notifications or map pin"
                className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
                <Pencil size={15} /> Edit client
              </button>
            )}
            {statementUrl && (
              <a href={statementUrl} target="_blank" rel="noreferrer"
                title="Open the live statement this client sees from their WhatsApp link"
                className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium">
                <FileText size={15} /> Live statement
              </a>
            )}
          </div>
        </div>

        {/* Bank-matching keywords — editable here */}
        <div className="mt-4 pt-3 border-t">
          <KeywordEditor clientId={client.id} keywords={client.match_keywords || []}
            conflicts={keyword_conflicts || []} />
        </div>
      </div>

      {/* Parcels not yet billed: price them, or invoice them from here */}
      <UnbilledParcels client={client} rows={unbilled} filter={unbilledFilter} onFilter={setUnbilledFilter} />

      {/* Invoices */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <h2 className="font-semibold text-gray-700 px-5 pt-4 pb-2 flex items-center gap-2">
          <Receipt size={16} className="text-orange-500" /> Invoices
        </h2>
        {invoices.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No invoices yet for this client.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-2 text-left">Invoice</th>
                  <th className="px-4 py-2 text-left hidden sm:table-cell">Date</th>
                  <th className="px-4 py-2 text-left">Due</th>
                  <th className="px-4 py-2 text-right hidden sm:table-cell">Total</th>
                  <th className="px-4 py-2 text-right hidden sm:table-cell">Paid</th>
                  <th className="px-4 py-2 text-right">Balance</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Sent to client</th>
                  <th className="px-4 py-2 text-left hidden sm:table-cell">Parcels</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {invoices.map((i) => {
                  const isOverdue = ['unpaid', 'part_paid'].includes(i.status)
                    && i.due_date && new Date(i.due_date) < new Date();
                  const sentCell = i.status === 'cancelled' ? <span className="text-gray-300">—</span>
                    : i.last_send_status === 'sent'
                      ? <span className="text-green-700" title={`${i.send_count} email${i.send_count === 1 ? '' : 's'}${i.last_sent_by_name ? ` · last by ${i.last_sent_by_name}` : ''}`}>✉ Emailed {dmy(i.last_sent_at)}<span className="text-gray-400 block text-[11px] truncate max-w-[12rem]">{i.last_sent_to}</span></span>
                      : i.last_send_status === 'bounced'
                        ? <span className="text-red-600 font-semibold" title={i.last_sent_to}>⚠ Bounced — {i.last_sent_to}<span className="text-gray-400 block text-[11px] font-normal">emailed {dmy(i.last_sent_at)}, address rejected it</span></span>
                      : i.last_send_status === 'failed'
                        ? <span className="text-red-600" title={i.last_sent_to}>✉ Email failed {dmy(i.last_sent_at)}</span>
                        : i.print_count > 0
                          ? <span className="text-gray-700">🖨 Printed {dmy(i.last_printed_at)}<span className="text-gray-400 block text-[11px]">not emailed</span></span>
                          : i.source === 'quickbooks'
                            ? <span className="text-gray-400 text-[11px]">sent from QuickBooks</span>
                            : <span className="text-amber-600 font-medium">Not sent</span>;
                  const parcelsCell = i.parcel_count > 0
                    ? <span className={i.parcels_delivered >= i.parcel_count ? 'text-green-700' : 'text-amber-700'}
                        title={`${i.parcels_delivered} of ${i.parcel_count} parcels delivered`}>
                        {i.parcels_delivered >= i.parcel_count ? '✓ ' : ''}{i.parcels_delivered}/{i.parcel_count} delivered
                      </span>
                    : <span className="text-gray-300">—</span>;
                  return (
                    <tr key={i.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-mono text-xs font-semibold text-gray-800">
                        {i.invoice_number}
                        {i.source === 'quickbooks' && (
                          <span title={`Imported from QuickBooks · original number ${i.source_ref || ''}`}
                            className="ml-1.5 font-sans text-[10px] bg-gray-100 text-gray-500 rounded px-1 py-0.5">QuickBooks</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-gray-500 hidden sm:table-cell">{dmy(i.created_at)}</td>
                      <td className={`px-4 py-2.5 ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                        {dmy(i.due_date)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-700 hidden sm:table-cell">{Rand(i.total)}</td>
                      <td className="px-4 py-2.5 text-right text-green-600 hidden sm:table-cell">{Rand(i.paid_amount)}</td>
                      <td className={`px-4 py-2.5 text-right font-semibold ${
                        parseFloat(i.balance) > 0.005 ? 'text-orange-600' : 'text-gray-400'}`}>
                        {Rand(i.balance)}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full ${INVOICE_STATUS[i.status] || ''}`}>
                          {INVOICE_LABEL[i.status] || i.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-xs">{sentCell}</td>
                      <td className="px-4 py-2.5 text-xs hidden sm:table-cell">{parcelsCell}</td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <button onClick={() => setPreviewId(i.id)} title="Preview this invoice here (nothing is downloaded or recorded)"
                          className="text-gray-400 hover:text-orange-500 mr-3">
                          <Eye size={15} />
                        </button>
                        <button onClick={() => openInvoicePdf(i.id)} title="Download this invoice as a PDF"
                          className="text-gray-400 hover:text-orange-500">
                          <FileText size={15} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Statements emailed */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <h2 className="font-semibold text-gray-700 px-5 pt-4 pb-2 flex items-center gap-2">
          <Mail size={16} className="text-orange-500" /> Statements emailed
        </h2>
        {statements.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">
            No statement has been emailed to this client yet. Send one from the Month-end page.
          </p>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="px-4 py-2 text-left">Month</th>
                <th className="px-4 py-2 text-left hidden sm:table-cell">Sent to</th>
                <th className="px-4 py-2 text-right">Closing balance</th>
                <th className="px-4 py-2 text-left">Sent</th>
                <th className="px-4 py-2 text-left hidden sm:table-cell">By</th>
                <th className="px-4 py-2 text-left">Result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {statements.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-medium text-gray-700">
                    {new Date(s.period_month).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })}
                  </td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs hidden sm:table-cell">{s.sent_to}</td>
                  <td className="px-4 py-2.5 text-right text-gray-700">{Rand(s.closing_balance)}</td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs">
                    {new Date(s.sent_at).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })}
                  </td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs hidden sm:table-cell">{s.sent_by_name || '—'}</td>
                  <td className="px-4 py-2.5">
                    {s.status === 'sent' ? (
                      <span className="inline-flex items-center gap-1 text-green-600 text-xs">
                        <CheckCircle size={13} /> Sent
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-600 text-xs" title={s.error || ''}>
                        <AlertTriangle size={13} /> Failed
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
